import { all, call, put, takeEvery, select, take, race } from 'typed-redux-saga';
import { PayloadAction } from '@reduxjs/toolkit';

import { uploadFailed, imageResized } from 'src/components/alert/slice';
import { getRegionSetting } from 'src/selectors';
import { SCREEN } from 'src/config/constants';
import { uploadImageAndGetUrl, getImageSize, toSafeUploadName } from 'src/util/upload';
import { logError } from 'src/loggingManager';
import { uploadOverlays, overlayUploaded, removeOverlay, uploadAndReplaceOverlay, replaceOverlayFailed } from './slice';
import { showResizeModal, acceptResize, declineResize, cancelResizeModalUpload } from '../backgrounds/slice';

const BACKGROUND_WARNING_THRESHOLD = 2000;
const BACKGROUND_MAX_THRESHOLD = 6000;

type ResizeParams = {
    height: number;
    width: number;
};

type UploadedOverlay = {
    file: File;
    id: string;
};

function imageOverMaxDimension(height: number, width: number, maxDimension: number): boolean {
    return width > maxDimension || height > maxDimension;
}

function calculateResizedOverlayDimensions(height: number, width: number, maxDimension: number): ResizeParams {
    let newHeight = height;
    let newWidth = width;
    if (height > width && height > maxDimension) {
        newHeight = maxDimension;
        newWidth = Math.round((maxDimension / height) * width);
    } else if (width > height && width > maxDimension) {
        newWidth = maxDimension;
        newHeight = Math.round((maxDimension / width) * height);
    } else if (height === width && height > maxDimension) {
        newHeight = maxDimension;
        newWidth = maxDimension;
    }

    return { height: newHeight, width: newWidth };
}

function* checkImageSize(height: number, width: number): Generator<any, [boolean | null, boolean | null], any> {
    let resizeImage = false;
    let resizedTooLarge = false;
    if (imageOverMaxDimension(height, width, BACKGROUND_MAX_THRESHOLD)) {
        resizeImage = true;
        resizedTooLarge = true;
    } else if (imageOverMaxDimension(height, width, BACKGROUND_WARNING_THRESHOLD)) {
        yield* put(showResizeModal('large'));
        const { acceptedResize, declinedResize, cancel } = yield* race({
            acceptedResize: take(acceptResize),
            declinedResize: take(declineResize),
            cancel: take(cancelResizeModalUpload),
        });

        if (cancel) {
            return [null, null];
        }

        if (acceptedResize) {
            resizeImage = true;
        } else if (declinedResize) {
            resizeImage = false;
        }
    }

    return [resizeImage, resizedTooLarge];
}

function* uploadOverlay(file: File, id: string): Generator<any, void, any> {
    let { height, width } = yield* call(getImageSize, file);
    let url: string;

    const [resizeImage, resizedTooLarge] = yield* call(checkImageSize, height, width);

    if (resizeImage === null) {
        // "Cancel" was clicked
        return;
    }

    try {
        const region = yield* select(getRegionSetting);

        let resizeParams: ResizeParams | undefined;
        if (resizeImage) {
            resizeParams = calculateResizedOverlayDimensions(height, width, BACKGROUND_WARNING_THRESHOLD);
            height = resizeParams.height;
            width = resizeParams.width;
        }

        url = (yield* call(uploadImageAndGetUrl as any, file, region, resizeParams)) as string;
    } catch (error: any) {
        yield* put(uploadFailed(`Overlay: ${error}`));
        throw error;
    }

    if (resizedTooLarge) {
        yield* put(imageResized());
    }

    yield* put(
        overlayUploaded({
            id,
            url,
            name: toSafeUploadName(file.name),
            mode: SCREEN,
        }),
    );
}

function* addOverlay({ file, id }: UploadedOverlay): Generator<any, void, any> {
    try {
        yield* call(uploadOverlay, file, id);
    } catch (error: any) {
        logError(
            `unable to add overlay, there was an error while uploading/adding: ${error} ${file.name} ${file.size} ${file.type}`,
        );
        yield* put(removeOverlay(id));
    }
}

function* replaceOverlay({ payload }: PayloadAction<{ file: File; id: string }>): Generator<any, void, any> {
    const { file, id } = payload;
    try {
        yield* call(uploadOverlay, file, id);
    } catch (error: any) {
        logError(
            `unable to replace overlay, there was an error while uploading/updating: ${error} ${file.name} ${file.size} ${file.type}`,
        );
        yield* put(replaceOverlayFailed(id));
    }
}

function* addOverlays({ payload }: PayloadAction<UploadedOverlay[]>): Generator<any, void, any> {
    yield* all(payload.map((overlay) => call(addOverlay, overlay)));
}

export default function* overlaysSagas() {
    return yield* all([
        yield* takeEvery(uploadOverlays as any, addOverlays),
        yield* takeEvery(uploadAndReplaceOverlay, replaceOverlay),
    ]);
}
